/**
 * Convert server pendingHarralMindLeechUnitStealFlyAnimations to viewer-relative fly grant payloads.
 */

const HARRAL_MIND_LEECH_UNIT_STEAL_FLY_SOURCE = 'harral_mind_leech_378';

function buildStolenUnitFlySnapshot(unit) {
  if (!unit?.instanceId) {
    throw new Error('buildStolenUnitFlySnapshot: unit.instanceId is required');
  }
  if (unit.id == null) {
    throw new Error('buildStolenUnitFlySnapshot: unit.id is required');
  }
  return {
    id: unit.id,
    name: unit.name,
    instanceId: unit.instanceId,
    type: unit.type || 'unit',
    health: unit.health,
    attack_rating: unit.attack_rating,
    damage: unit.damage ?? unit.attack_rating,
    isPlayable: false,
    isOOR: unit.isOOR === true,
  };
}

function mapBattlefieldSlotToViewer(playerId, zone, slotIndex, viewerUserId) {
  if (playerId == null || zone == null || slotIndex == null) {
    throw new Error('mapBattlefieldSlotToViewer: playerId, zone, and slotIndex are required');
  }
  const prefix = String(playerId) === String(viewerUserId) ? 'your' : 'opponent';
  return {
    zone: `${prefix}_${zone}`,
    index: slotIndex,
  };
}

/**
 * Seed source-slot suppress keys BEFORE other VFX / board death detection.
 * Does not consume pending animations (queueGrants still owns playback).
 */
function seedHarralMindLeechStealSuppressFromPending(
  gameStateFragment,
  viewerUserId,
  suppressSourceSlotKeysRef,
  clearSlotPreviousStateRef = null
) {
  const pending = gameStateFragment?.pendingHarralMindLeechUnitStealFlyAnimations;
  if (!pending?.length) {
    return [];
  }
  if (viewerUserId == null) {
    throw new Error('seedHarralMindLeechStealSuppressFromPending: viewerUserId is required');
  }
  if (!suppressSourceSlotKeysRef?.current) {
    throw new Error('seedHarralMindLeechStealSuppressFromPending: suppressSourceSlotKeysRef is required');
  }

  const seededSlotKeys = [];
  pending.forEach((anim) => {
    if (
      anim.sourcePlayerId == null
      || anim.sourceZone == null
      || anim.sourceSlotIndex == null
    ) {
      throw new Error(
        `seedHarralMindLeechStealSuppressFromPending: source slot required for ${anim.animationKey || 'unknown'}`
      );
    }
    const fromSlot = mapBattlefieldSlotToViewer(
      anim.sourcePlayerId,
      anim.sourceZone,
      anim.sourceSlotIndex,
      viewerUserId
    );
    const slotKey = `${fromSlot.zone}_${fromSlot.index}`;
    suppressSourceSlotKeysRef.current.add(slotKey);
    seededSlotKeys.push(slotKey);
    if (typeof clearSlotPreviousStateRef?.current === 'function') {
      clearSlotPreviousStateRef.current(fromSlot.zone, fromSlot.index);
    }
  });
  return seededSlotKeys;
}

function consumePendingHarralMindLeechUnitStealFlyAnimations(gameStateFragment, viewerUserId) {
  if (!gameStateFragment?.pendingHarralMindLeechUnitStealFlyAnimations?.length) {
    return [];
  }
  if (viewerUserId == null) {
    throw new Error('consumePendingHarralMindLeechUnitStealFlyAnimations: viewerUserId is required');
  }

  const pending = [...gameStateFragment.pendingHarralMindLeechUnitStealFlyAnimations];
  delete gameStateFragment.pendingHarralMindLeechUnitStealFlyAnimations;

  return pending.map((anim) => {
    if (!anim.animationKey) {
      throw new Error('consumePendingHarralMindLeechUnitStealFlyAnimations: animationKey is required');
    }
    if (
      anim.sourceZone == null
      || anim.sourceSlotIndex == null
      || anim.destZone == null
      || anim.destSlotIndex == null
    ) {
      throw new Error(
        `consumePendingHarralMindLeechUnitStealFlyAnimations: slots required for ${anim.animationKey}`
      );
    }

    const fromSlot = mapBattlefieldSlotToViewer(
      anim.sourcePlayerId,
      anim.sourceZone,
      anim.sourceSlotIndex,
      viewerUserId
    );
    const toSlot = mapBattlefieldSlotToViewer(
      anim.destPlayerId,
      anim.destZone,
      anim.destSlotIndex,
      viewerUserId
    );

    return {
      key: anim.animationKey,
      fromZone: fromSlot.zone,
      fromIndex: fromSlot.index,
      toZone: toSlot.zone,
      toIndex: toSlot.index,
      stolenUnitInstanceId: anim.stolenUnitInstanceId,
      source: anim.source || HARRAL_MIND_LEECH_UNIT_STEAL_FLY_SOURCE,
      unitSnapshot: buildStolenUnitFlySnapshot({
        id: anim.stolenUnitId,
        name: anim.stolenUnitName,
        instanceId: anim.stolenUnitInstanceId,
        type: 'unit',
      }),
    };
  });
}

module.exports = {
  HARRAL_MIND_LEECH_UNIT_STEAL_FLY_SOURCE,
  seedHarralMindLeechStealSuppressFromPending,
  consumePendingHarralMindLeechUnitStealFlyAnimations,
};
